// HubGrid — Grille filtrée réutilisable pour les 4 hubs
// Migré vers OHGrid v1.1.1 (GROOT UI-002/004/005/006/007)
// Endpoint: /api/metier/hub-grids/grid/{hub_name}
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MettreDeCoteDialog, mettreDeCote } from "./dossier/MettreDeCoteDialog";
import { isInstallateurProfile, getBusinessModel, hasDossierAmo,
         hasDevisInstallateur } from "../../utils/tenantProfile";
import { OMV_DOSSIER_COLUMNS } from "../../lib/omvDossier";
// #557 — source UNIQUE du filtrage tri-état, partagée par toutes les grilles.
import { matchTriEtat, matchEtatFacture } from "../../lib/filtreTriEtat";
import {
  CheckCircle2, XCircle, Download, Eye, Archive, ArchiveRestore, Trash2, Package, Send, Plus, FileText,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatDateFR } from "../../utils/dateFormat";
import { hasIntegrationPennylane, profilChantier } from "../../utils/tenantProfile";
import { bulkSetSigned, bulkSetBoolField, bulkSetFacturePayee, BulkApplyProduitAmoModal, bulkCreateInvoices, bulkCreateDevis } from "./BulkEditModals";
import GamoraHubBadge from "../../components/governance/GamoraHubBadge";

// hubName HubGrid → hub Gamora (clés alignées sans accent : tous les appelants
// passent "conformite" sans accent, cf. HUB_FILTERS backend ; l'ancienne clé
// "conformité" accentuée ne matchait jamais → badge Gamora muet pour Conformité).
const GAMORA_HUB_MAP = {
  commercial: "commercial",
  operation: "operation",
  conformite: "conformite",
  finance: "finance",
};

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

// 🔴🔴 N°728-7 — LA GRILLE DOSSIERS SERVAIT SES COLONNES AMO PAR DÉFAUT.
//
// ⚠️ MESURÉ en PROD (INTÉGRATION, compte mario_bat) : le choix des colonnes est
// `isOmv ? grille CEE : override par slug : HUB_COLUMNS` — une règle EN CREUX.
// Mario n'est ni installateur ni listé, il RETOMBAIT donc sur la grille AMO et
// lisait « Contrat AMO », « Montant AMO », « Facture AMO », « Installateur »,
// « Visite payée » sur ses propres chantiers.
//
// 🔑 CE QUE LE LOT FAIT, ET CE QU'IL ⛔ NE FAIT PAS :
//   · il RETIRE les colonnes dont le CONCEPT n'est pas déclaré, et SEULEMENT
//     quand le tenant ⛔ n'a AUCUNE grille à lui (⛔ ni famille installateur,
//     ⛔ ni override par slug) — c'est-à-dire le cas de Mario, et lui seul ;
//   · ⛔ il ⛔ ne touche PAS tef (il déclare l'AMO), ⛔ ni omv/climat (ils ont
//     leur grille CEE) : « EXACTEMENT ce qu'ils voyaient » est tenu.
// ⚠️ DETTE NOMMÉE, ⛔ NON PRISE : sur les hubs Opération et Finance, omv et
// climat retombent EUX AUSSI sur la grille AMO (⛔ aucune grille CEE n'y est
// déclarée). Les dépolluer là exigerait de leur RETIRER des colonnes visibles
// aujourd'hui — ⛔ hors du périmètre « non-régression stricte » de ce lot.
const CAPACITE_PAR_COLONNE = {
  contrat_signe: "dossier_amo",            // « Contrat AMO »
  mandat_signe: "dossier_amo",             // mandat ANAH
  visite_payee: "dossier_amo",             // le donneur d'ordre a payé le visiteur
  montant_amo: "dossier_amo",
  reference_facture_amo: "dossier_amo",
  facture_payee: "dossier_amo",
  date_paiement_display: "dossier_amo",
  installateur_nom: "devis_installateur",
  has_devis_installateur: "devis_installateur",
  // 🔴 P-MARIO-19 — trois colonnes AMO restées UNIVERSELLES, mesurées à l'écran Mario :
  //   · `visiteur_amo` : le visiteur ASSIGNÉ par le parcours AMO (DAL, visite payée).
  //     ⚠️ 0 dossier mario_bat le renseigne en preprod : sa visite (relevé chantier)
  //     porte un opérateur AILLEURS. Colonne toujours vide, filtre sans valeur.
  //   · `prochaine_action` : calculée sur le pipeline AMO (« Assigner visiteur »,
  //     « Déposer ANAH », « Demander l'avance »…) — Mario lisait « Assigner visiteur ».
  //   · `type_projet` : c'est `type_aide` (le dispositif d'aide), « — » chez Mario.
  visiteur_amo: "dossier_amo",
  prochaine_action: "dossier_amo",
  type_projet: "dossier_amo",
};

const A_LA_CAPACITE = {
  dossier_amo: hasDossierAmo,
  devis_installateur: hasDevisInstallateur,
};

// 🔴🔴 N°756-2 — LES FILTRES DU DASHBOARD DISAIENT LA MÊME CHOSE QUE LES
// COLONNES. ⚠️ MESURÉ en PROD par INTÉGRATION sur mario_bat (Nouveau dossier →
// Dashboard → Filtres) : « Tous — Contrat AMO », « Tous — Installateur »,
// « Tous — Mandataire ». Le jeu de filtres est choisi par `isOmv ? … : …` — la
// MÊME règle en creux que les colonnes : Mario, qui n'est ni installateur ni
// listé, retombait sur le jeu complet d'AMO.
// 🔑 Même registre, même portée : ⛔ on ⛔ ne filtre QUE la retombée par défaut
// (tef garde tout — il déclare tout ; omv et climat ont leur propre jeu).
const CAPACITE_PAR_FILTRE = {
  filterContratSigneLocal: "dossier_amo",    // « Contrat AMO »
  filterMandatSigneLocal: "dossier_amo",     // mandat ANAH
  filterMandataireLocal: "dossier_amo",      // mandataire ANAH
  filterVisitePayeeLocal: "dossier_amo",     // le donneur d'ordre a payé le visiteur
  filterInstallateurLocal: "devis_installateur",
  filterDevisInstLocal: "devis_installateur",
  // 🔴 P-MARIO-19 — mêmes concepts que leurs colonnes (⛔ colonne retirée + filtre
  // resté = incohérence) : « Visiteur » lit `visiteur_amo` ; « Facture payée » lit
  // `facture_payee_etat`, l'état de la FACTURE AMO (Pennylane) — la colonne
  // `facture_payee` était déjà gatée, son filtre ne l'était pas.
  filterVisiteurLocal: "dossier_amo",
  filterFacturePayeeLocal: "dossier_amo",
};

/** Retire les filtres dont la capacité n'est ⛔ PAS déclarée. Pur. */
export function filtresSelonCapacites(filtres) {
  return (filtres || []).filter((f) => {
    const requise = CAPACITE_PAR_FILTRE[f.key];
    if (!requise) return true;                       // filtre universel
    const aLaCapacite = A_LA_CAPACITE[requise];
    return !!aLaCapacite && aLaCapacite();           // inconnue ⇒ retiré
  });
}

/** 🔴 P1 (organisation validée JJ : 1 SITE PAR DOSSIER) — colonne « Site / Adresse », SEULEMENT pour un tenant à profil chantier (config `profil_chantier`, ⛔ pas de slug) :
 *  les autres tenants gardent EXACTEMENT leurs colonnes. Insérée après « Client ». Pur. */
export function colonnesAvecSite(colonnes, aProfilChantier) {
  if (!aProfilChantier) return colonnes;
  if ((colonnes || []).some((c) => c.key === "site_libelle")) return colonnes;
  const i = (colonnes || []).findIndex((c) => c.key === "client_nom");
  const col = { key: "site_libelle", label: "Site / Adresse", sortable: true };
  return i < 0 ? [...colonnes, col] : [...colonnes.slice(0, i + 1), col, ...colonnes.slice(i + 1)];
}

/** 🔴 Numérotation Mario — le LIBELLÉ du dossier « AAAA-DOS-NNNNN - NOM DU CLIENT » remplace la colonne « Réf » pour un tenant à profil chantier (config `profil_chantier`, ⛔ pas de slug) ;
 *  les autres tenants gardent EXACTEMENT « Réf ». Les anciens dossiers (MARI-DOS-2026-0000N) s'affichent tels quels, suivis du client. Pur. */
export function colonneLibelleDossier(colonnes, aProfilChantier) {
  if (!aProfilChantier) return colonnes;
  return (colonnes || []).map((c) => (c.key === "reference" ? { ...c, key: "libelle_dossier", label: "Dossier" } : c));
}
/** PN-10 — aide de la recherche : le SIRET et l'établissement sont du vocabulaire OMV (profil installateur), jamais montrés à un tenant AMO. Pur. */
export function placeholderRecherche(profilInstallateur) {
  return profilInstallateur
    ? "Rechercher par référence, client, ville, SIRET, établissement..."
    : "Rechercher par référence, client, ville, visiteur, installateur...";
}
/** PN-10 — état vide qui explique : les filtres, ou des dossiers mis de côté, ou rien du tout. Pur. */
export function messageVide({ filtresActifs, nbMisDeCote }) {
  if (filtresActifs) return "Aucun dossier avec ces filtres";
  if (nbMisDeCote > 0) return `Aucun dossier à afficher : ${nbMisDeCote} dossier${nbMisDeCote > 1 ? "s sont mis" : " est mis"} de côté`;
  return "Aucun dossier pour le moment";
}
export function libelleDuDossier(dossier) {
  const ref = String(dossier?.reference || "").trim();
  const client = String(dossier?.client_nom || "").trim();
  return ref && client ? `${ref} - ${client}` : ref || client || "—";
}

/** Libellé du site d'un dossier : « adresse, CP ville », « — » s'il n'a pas de site. `sites` = {id applicatif → site}. Pur. */
export function libelleSiteDuDossier(dossier, sites) {
  if (!dossier?.site_id) return "—";
  const s = (sites || {})[dossier.site_id];
  if (!s) return "…";
  return [s.adresse, [s.code_postal, s.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ");
}

/** 🔴 P-MARIO-19 — ⛔ FILTRE VIDE : une liste sans AUCUNE valeur ne filtre rien, elle
 *  promet un tri qui n'existe pas. Retire les `select` sans option. Pur.
 *  ⚠️ N'est appliqué qu'à la RETOMBÉE d'un tenant qui ⛔ ne déclare PAS le jeu AMO : le
 *  jeu AMO est le jeu DÉCLARÉ de ce tenant, il reste EXACTEMENT tel quel. */
/** Onglets à afficher : « all » reste toujours ; un onglet de phase n'existe que si le tenant
 *  porte au moins un état dans cette phase (`onglets` renvoyé par le serveur). Avant la
 *  première réponse (onglets inconnus), on garde la liste telle que reçue. */
export function presetsVisibles(presets, onglets) {
  if (!Array.isArray(presets)) return presets;
  if (!Array.isArray(onglets)) return presets;
  return presets.filter((p) => p.key === "all" || onglets.includes(p.key));
}

export function sansFiltreVide(filtres) {
  return (filtres || []).filter((f) => f.type !== "select" || (f.options || []).length > 0);
}

/** 🔴 P-MARIO-19 — OPTIONS DU FILTRE « Statut ».
 *  · Le tenant DÉCLARE sa liste `statuts_dossier` ⇒ inchangé : libellés par défaut
 *    fusionnés avec la liste (le comportement historique, ⛔ non modifié).
 *  · ⛔ Aucune liste déclarée ⇒ ⛔ on n'invente PAS un pipeline : les options sont les
 *    étapes RÉELLEMENT portées par les dossiers chargés (`pipeline_step`, la valeur
 *    même de la colonne « Statut »), libellées comme la colonne. Avant, un tel tenant
 *    recevait les 15 libellés par défaut — dont « Prêt dépôt », « Déposé » (×2),
 *    « Instruction », « Octroi » : le vocabulaire ANAH. Pur. */
export function optionsStatutDossier(stepLabels, statutsDeclares, dossiers) {
  if ((statutsDeclares || []).length > 0) {
    return Object.entries(stepLabels || {}).map(([k, lab]) => ({ value: k, label: lab }));
  }
  const vus = [];
  (dossiers || []).forEach((d) => {
    const k = d && d.pipeline_step;
    if (k && !vus.includes(k)) vus.push(k);
  });
  return vus.map((k) => ({ value: k, label: (stepLabels || {})[k] || k.replace(/_/g, " ") }));
}

/** T-070 — VOCABULAIRE DU TENANT. Le serveur renvoie `etats` : les états de `metier_statuts_config`
 *  du tenant (code, libellé, phase, nombre de dossiers). Les pastilles, la colonne « Statut », le
 *  filtre « Statut » et l'export CSV lisent CETTE liste — plus de pipeline TEF commun. Purs. */

/** Libellé affiché pour la ligne : celui du tenant (`statut_libelle`), sinon le libellé par défaut
 *  de l'ancien vocabulaire, sinon le code lisible. */
export function libelleStatutLigne(row, stepLabels) {
  if (row && row.statut_libelle) return row.statut_libelle;
  const code = row && (row.pipeline_step || row.statut);
  return (stepLabels || {})[code] || (code ? String(code).replace(/_/g, " ") : "—");
}

/** Couleurs d'un état d'après sa PHASE : paires fond/texte/bord `--oh-*` des deux modes. */
export function couleursPhase(phase) {
  const paire = (n) => ({ bg: `bg-[var(--oh-${n}-bg)]`, border: `border-[var(--oh-${n}-border)]`, text: `text-[var(--oh-${n}-text)]` });
  switch (phase) {
    case "commercial": return { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" };
    case "operation": return paire("stat-cyan");
    case "conformite": return paire("info");
    case "finance": return paire("stat-amber");
    case "cloture": return paire("success");
    case "abandonne": return paire("error");
    default: return null;
  }
}

/** Famille de couleur des cartes de stats (oh-grid) d'un état d'après sa phase. */
export function familleStat(phase) {
  if (phase === "abandonne") return "red";
  if (phase === "cloture") return "green";
  if (phase === "operation") return "cyan";
  if (phase === "finance") return "amber";
  return "blue";
}

/** Cartes de stats cliquables = un état du tenant chacune, dans l'ordre de sa configuration. */
export function statsDepuisEtats(etats) {
  return (etats || []).map((e) => ({
    key: `etat_${e.code}`,
    label: e.label || e.code,
    value: e.count,
    color: familleStat(e.phase),
    filter: (row) => row.statut === e.code,
  }));
}

/** T-482 : seul le profil `omv` (poseur B2B) reçoit la grille CEE du dossier ; le profil `installateur` (Climat, B2C) ne l'a pas. Pur. */
export function grilleCEEPourProfil(businessModel) {
  return String(businessModel || "").trim().toLowerCase() === "omv";
}

/** T-430c : une pastille d'état compte TOUS les dossiers du serveur, y compris mis de côté et abandonnés, que la liste par défaut n'a pas chargés.
 *  Seules « Total » et « Actifs » se contentent de la liste par défaut ; toute autre pastille demande aussi les mis de côté. Pur. */
export function pastilleExigeMisDeCote(cle) {
  return !!cle && cle !== "all" && cle !== "active";
}

/** Options du filtre « Statut » quand le serveur a fourni les états du tenant ; sinon `null`. */
export function optionsStatutDepuisEtats(etats) {
  if (!Array.isArray(etats) || etats.length === 0) return null;
  return etats.map((e) => ({ value: e.code, label: e.label || e.code }));
}

/** 🔴 ACTIONS DE MASSE — même registre que les colonnes : une action qui ÉCRIT un
 *  champ (`champ`) exige la capacité de ce champ (CAPACITE_PAR_COLONNE). Sans
 *  `champ` ⇒ action générique (archiver, supprimer…). Capacité absente ⇒ retirée
 *  (Mario ne voyait « Cocher Contrat AMO / Mandat / Visite payée / Facture payée »
 *  que parce qu'il n'était pas OMV). Pur. */
export function actionsMasseSelonCapacites(actions) {
  return (actions || []).filter((a) => {
    const requise = a.champ && CAPACITE_PAR_COLONNE[a.champ];
    if (!requise) return true;                       // action générique
    const aLaCapacite = A_LA_CAPACITE[requise];
    return !!aLaCapacite && aLaCapacite();           // inconnue ⇒ retirée
  });
}

/** Retire les colonnes dont la capacité n'est ⛔ PAS déclarée. Pur. */
export function colonnesSelonCapacites(colonnes) {
  return (colonnes || []).filter((c) => {
    const requise = CAPACITE_PAR_COLONNE[c.key];
    if (!requise) return true;                       // colonne universelle
    const aLaCapacite = A_LA_CAPACITE[requise];
    return !!aLaCapacite && aLaCapacite();           // inconnue ⇒ retirée
  });
}

// PN-8 : le super admin, lu dans la session (même source que AuthContext, qui garde `user` dans localStorage) — sans importer le contexte,
// que cette grille n'a jamais eu besoin de monter. Illisible ⇒ non super admin (fail-closed : la suppression définitive disparaît).
export function utilisateurSuperAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.role === "super_admin" || u?.is_super_admin === true;
  } catch { return false; }
}

// Colonnes par hub (spec métier validée) — sortable: true = triable
const HUB_COLUMNS = {
  commercial: [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "ville_cp", label: "Ville - CP", muted: true, sortable: true },
    { key: "pipeline_step", label: "Statut", badge: true, sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "contrat_signe", label: "Contrat AMO", check: true },
    { key: "mandat_signe", label: "Mandat", check: true },
    { key: "type_projet", label: "Type projet", muted: true, sortable: true },
    { key: "completude_pct", label: "Complétude", pct: true, sortable: true },
    { key: "prochaine_action", label: "Prochaine action", accent: true },
  ],
  operation: [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "installateur_nom", label: "Installateur", sortable: true },
    { key: "type_projet", label: "Type projet", muted: true, sortable: true },
    { key: "has_devis_installateur", label: "Devis installateur", check: true },
    { key: "has_photos", label: "Photos", check: true },
    { key: "date_visite", label: "Date visite", muted: true, sortable: true },
    { key: "statut_chantier", label: "Statut chantier", badge: true, sortable: true },
  ],
  conformite: [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "has_dal", label: "DAL", check: true },
    { key: "has_plan_financement", label: "Plan de financement", check: true },
    { key: "statut_principal", label: "Statut principal", badge: true, sortable: true },
    { key: "date_depot_anah", label: "Date dépôt", muted: true, sortable: true },
    { key: "date_octroi", label: "Date octroi", muted: true, sortable: true },
    { key: "montant_aides_display", label: "Montant aides", money: true, sortable: true },
    { key: "completude_pct", label: "Checklist", pct: true, sortable: true },
    { key: "blocage", label: "Blocage", warning: true },
  ],
  finance: [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "visiteur_amo", label: "Visiteur", sortable: true },
    { key: "visite_payee", label: "Visite payée", check: true },
    { key: "montant_amo", label: "Montant AMO", money: true, sortable: true },
    { key: "reference_facture_amo", label: "Facture AMO" },
    { key: "facture_payee", label: "Facture payée", check: true },
    { key: "date_paiement_display", label: "Date paiement", muted: true, sortable: true },
  ],
  all: [
    { key: "reference", label: "Référence", mono: true, sortable: true },
    { key: "client_nom", label: "Client", sortable: true },
    { key: "pipeline_step", label: "Statut", badge: true, sortable: true },
    { key: "ville_cp", label: "Ville - CP", muted: true, sortable: true },
    { key: "created_at", label: "Date création", muted: true, sortable: true },
    { key: "installateur_nom", label: "Installateur", sortable: true },
    { key: "completude_pct", label: "Complétude", pct: true, sortable: true },
    { key: "prochaine_action", label: "Prochaine action", accent: true },
  ],
};

// ─── Colonnes paramétrables PAR TENANT (refonte Métier/Tenant) ──────────────
// Override de HUB_COLUMNS pour un tenant donné. Gaté tenant : n'affecte QUE les
// tenants listés ; TEF/climat conservent HUB_COLUMNS à l'identique.
// OMV (installateur CEE) : field-names réels (spec SPEC-CEE-OMV-DOSSIER-2026-06-07,
// schéma models.py 6e70d820f). Émis par hub_grids.py uniquement pour omv_france.
// VMC : valeurs métier (technique.type_vmc) → libellés lisibles.
const VMC_LABELS = { simple_flux: "Simple flux", double_flux: "Double flux" };
// OMV_DOSSIER_COLUMNS extrait dans lib/omvDossier.js (logique pure, testée :
// la 1re colonne "N° dossier" pointe sur `reference`, plus sur numero_dossier).
// Cf. AUDIT source réelle numero_dossier 2026-06-12.
const TENANT_HUB_COLUMNS = {
  omv_france: { commercial: OMV_DOSSIER_COLUMNS, all: OMV_DOSSIER_COLUMNS },
};

// Tenant courant — source canonique = localStorage.current_tenant_slug,
// posée par OmniHub.jsx depuis le backend (users.current_tenant), même clé
// que getCurrentTenantSlug()/getAuthHeaders. L'ancienne lecture de
// `currentEntreprise` était cassée : cette clé n'est JAMAIS persistée en
// localStorage (uniquement un useState React dans OmniHub.jsx) → le helper
// retombait toujours sur "tef" et l'override TENANT_HUB_COLUMNS[omv_france]
// ne s'appliquait jamais pour Solène (bug colonnes AMO en grille OMV).
const currentTenantSlug = () => {
  try {
    return localStorage.getItem("current_tenant_slug") || "";
  } catch { return ""; }
};

const HUB_META = {
  commercial: { label: "Hub Commercial", color: "var(--oh-hub-commercial)" },
  operation: { label: "Hub Opération", color: "var(--oh-hub-operation)" },
  conformite: { label: "Hub Conformité", color: "var(--oh-hub-conformite)" },
  finance: { label: "Hub Finance", color: "var(--oh-hub-finance)" },
  all: { label: "Tous les dossiers", color: "var(--oh-accent, var(--oh-stat-cyan-text))" },
};

// Défauts visuels uniquement — les vraies valeurs viennent de useConfigList (governance)
const DEFAULT_STEP_LABELS = {
  dossier_cree: "Créé",
  visite_planifiee: "Planifié",
  visite_faite: "Visite faite",
  chantier_a_planifier: "Chantier à planifier",
  pret_depot: "Prêt dépôt",
  déposé: "Déposé",
  depose: "Déposé",
  instruction: "Instruction",
  octroi: "Octroi",
  travaux: "Travaux",
  clôture: "Clôturé",
  cloture: "Clôturé",
  refuse: "Refusé",
  abandonne: "Abandonné",
  rejet_abandon: "Rejeté/Abandonné",
};

const STEP_COLORS = {
  dossier_cree: { bg: "bg-slate-500/20", border: "border-[var(--oh-border)]", text: "text-[var(--oh-text-secondary)]" },
  visite_planifiee: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  visite_faite: { bg: "bg-[var(--oh-stat-cyan-bg)]", border: "border-[var(--oh-stat-cyan-border)]", text: "text-[var(--oh-stat-cyan-text)]" },
  chantier_a_planifier: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  pret_depot: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  déposé: { bg: "bg-[var(--oh-accent-bg)]", border: "border-[var(--oh-accent)]", text: "text-[var(--oh-accent-text)]" },
  instruction: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  octroi: { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  travaux: { bg: "bg-[var(--oh-info-bg)]", border: "border-[var(--oh-info-border)]", text: "text-[var(--oh-info-text)]" },
  clôture: { bg: "bg-[var(--oh-success-bg)]", border: "border-[var(--oh-success-border)]", text: "text-[var(--oh-success-text)]" },
  refuse: { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]" },
  abandonne: { bg: "bg-[var(--oh-error-bg)]", border: "border-[var(--oh-error-border)]", text: "text-[var(--oh-error-text)]" },
};

// ─── Cell renderers ────────────────────────────────────────────────────────
function CheckCell({ value }) {
  // #557 — TRI-ÉTAT, même doctrine que `residence_principale` (#546) : un `null`
  // affichait la même croix qu'un `false`, et la colonne affirmait « non » là où
  // personne n'avait rien saisi. On ne peut pas filtrer sur trois états et n'en
  // afficher que deux.
  if (value === true) {
    return <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" />;
  }
  if (value === false) {
    return <XCircle className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />;
  }
  return (
    <span className="text-[11px]" style={{ color: "var(--oh-stat-amber-text)" }}
          title="Non renseigné">—</span>
  );
}

function PctCell({ value }) {
  const pct = value || 0;
  // Couleur sémantique progress : vert OK, ambre warn, rouge danger.
  const pctColor = pct >= 80
    ? "var(--oh-stat-green-text)"
    : pct >= 50 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-red-text)";
  return (
    <div className="flex items-center gap-2">
      <div className="w-12 h-1.5 rounded-full" style={{ background: "var(--oh-border)" }}>
        <div className="h-full rounded-full"
          style={{ width: `${Math.min(pct, 100)}%`, background: pctColor }} />
      </div>
      <span className="text-xs font-medium" style={{ color: pctColor }}>{pct}%</span>
    </div>
  );
}

function BadgeCell({ value, row, stepLabels }) {
  const label = row?.statut_libelle || stepLabels[value] || value?.replace(/_/g, " ") || "—";
  const colors = couleursPhase(row?.statut_phase) || STEP_COLORS[value] || STEP_COLORS.dossier_cree;
  const isRefuse = value === "refuse";
  const anah = row?.statut_anah_label;
  const isAnah = row?.source_statut === "anah";
  const isAmoOverride = row?.source_statut === "amo_override";
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1">
        <Badge className={`text-[10px] px-1.5 py-0 ${colors.bg} ${colors.border} ${colors.text} ${isRefuse ? "font-medium animate-pulse" : ""}`}
          data-testid={`badge-statut-${value}`}>{label}</Badge>
        {isAmoOverride && (
          <Badge className="text-[9px] px-1 py-0 bg-[var(--oh-info-bg)] border-[var(--oh-info-border)] text-[var(--oh-info-text)]"
            data-testid="badge-amo-override">AMO</Badge>
        )}
        {(row?.inactif || row?.actif_inactif === "Inactif") && (
          <Badge className="text-[9px] px-1 py-0"
            style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)", border: "1px solid var(--oh-stat-amber-border)" }}
            data-testid="badge-inactif" title="Dossier inactif (gelé) — dimension orthogonale au statut">Inactif</Badge>
        )}
      </div>
      {isAnah && anah && anah !== label && (
        <span className="text-[9px] leading-tight" style={{ color: "var(--oh-text-muted)" }}>{anah}</span>
      )}
      {isAmoOverride && anah && (
        <span className="text-[9px] leading-tight text-[var(--oh-info-text)]">ANAH: {anah}</span>
      )}
      {/* PN-8 : un dossier mis de côté dit POURQUOI */}
      {row?.raison_archivage && (
        <span className="text-[10px] leading-tight break-words" style={{ color: "var(--oh-text-secondary)" }}
          data-testid="raison-mise-de-cote" title={row.raison_archivage}>Mis de côté : {row.raison_archivage}</span>
      )}
    </div>
  );
}

function AccentCell({ value }) {
  if (!value || value === "—") return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
  return <span className="text-xs font-medium" style={{ color: "var(--oh-stat-blue-text)" }}>{value}</span>;
}

function WarningCell({ value, row }) {
  const reasons = row?.needs_action_reasons || [];
  const hasAction = row?.needs_action && reasons.length > 0;
  const hasText = value && value.trim();
  if (!hasText && !hasAction) return <span style={{ color: "var(--oh-text-muted)" }}>—</span>;
  return (
    <div className="flex flex-col gap-0.5">
      {hasText && <span className="text-xs font-medium" style={{ color: "var(--oh-stat-red-text)" }} data-testid="blocage-text">{value}</span>}
      {hasAction && !hasText && (
        <span className="text-[10px] text-[var(--oh-info-text)]" data-testid="needs-action-indicator"
          title={reasons.map(r => r.label).join("\n")}>
          {reasons.length} action{reasons.length > 1 ? "s" : ""} requise{reasons.length > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────
export default function HubGrid({ hubName: hubNameProp, onOpenDossier, filterPreset, presets, facturationPennylane, etatsInitiaux = "" }) {
  // Preset = réglage interne de CETTE même grille (filtre statut HUB_FILTERS backend +
  // colonnes HUB_COLUMNS). On fusionne les ex-5 menus dossiers en 1 grille pilotée par
  // un sélecteur de presets : hubName devient un état local, hubNameProp en valeur
  // initiale. Les presets visibles sont calculés en amont (gating rôle/feature) et
  // passés en prop → aucun preset Finance/Conformité accessible à un rôle non autorisé.
  const [hubName, setHubName] = useState(hubNameProp);
  // PN-8 : « Mettre de côté » demande une raison (une boîte), pour une ligne ou pour la sélection ; la suppression définitive est réservée au super admin.
  const estSuperAdmin = utilisateurSuperAdmin();
  const [aMettreDeCote, setAMettreDeCote] = useState(null);   // { ids: [...] }
  useEffect(() => { setHubName(hubNameProp); }, [hubNameProp]);
  // T-180b — présélection « À facturer » de l'onglet Finance (même règle que la page « Dossiers à Facturer », lue en configuration du tenant côté serveur)
  const [aFacturer, setAFacturer] = useState(false);
  useEffect(() => { if (hubName !== "finance") setAFacturer(false); }, [hubName]);
  const presetEffectif = filterPreset || (aFacturer ? "a_facturer" : undefined);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total: 0, etats: [], dossiers: [] });
  const presetsAffiches = presetsVisibles(presets, data.onglets);
  const showPresets = Array.isArray(presetsAffiches) && presetsAffiches.length > 1;

  // Statuts depuis gouvernance (pas de hardcode)
  const { items: configStatuts } = useConfigList("statuts_dossier");
  const stepLabels = useMemo(() => {
    const merged = { ...DEFAULT_STEP_LABELS };
    configStatuts.forEach(s => { if (s.id && s.label) merged[s.id] = s.label; });
    return merged;
  }, [configStatuts]);

  // Mandataires : source unique = metier_config_listes.mandataires_tef
  // (éditable via Gouvernance > Listes > MetierListesPanel).
  const { items: configMandataires } = useConfigList("mandataires_tef");

  // Statuts OMV : liste éditable per-tenant (metier_config_listes type "statuts_omv",
  // éditée dans Module Aides > Variables et Listes). Champ libre, pas de workflow.
  // Inerte hors omv_france (la liste n'existe pas → []).
  const { items: omvStatuts } = useConfigList("statuts_omv");

  const meta = HUB_META[hubName] || HUB_META.commercial;
  // Colonnes tenant-aware : override par tenant si défini, sinon HUB_COLUMNS (TEF/climat inchangé).
  const tenantSlug = currentTenantSlug();
  const isOmv = isInstallateurProfile();
  // Colonnes : profil installateur (OMV, Climat) → grille CEE (cohérent avec isOmv qui
  // pilote bulk actions + filtres) ; sinon override par slug, sinon défaut (TEF inchangé).
  const _installateurCols = { commercial: OMV_DOSSIER_COLUMNS, all: OMV_DOSSIER_COLUMNS };
  // T-482 : la grille CEE (Établissement, Surface, Type VMC, Nb caissons) est celle du poseur B2B ; Climat (B2C, même famille) garde les filtres et actions de masse de la
  // famille mais reçoit les colonnes génériques du hub (voir `grilleCEEPourProfil`).
  const _colsDeclarees = (isOmv && grilleCEEPourProfil(getBusinessModel()) && _installateurCols[hubName])
    || (TENANT_HUB_COLUMNS[tenantSlug] && TENANT_HUB_COLUMNS[tenantSlug][hubName]);
  // 🔴 N°728-7 — LE PÉRIMÈTRE, ÉCRIT EXPLICITEMENT.
  // ⚠️ Le filtre ⛔ ne s'applique QU'AUX tenants qui ⛔ n'ont AUCUNE grille à eux
  // (⛔ ni famille installateur, ⛔ ni override par slug) : sinon omv et climat
  // perdraient des colonnes VISIBLES AUJOURD'HUI sur les hubs Opération et
  // Finance, où ils retombent aussi sur le défaut AMO — c'est la dette nommée
  // en tête de fichier, ⛔ pas ce lot. `tef`, lui, déclare l'AMO : le filtre le
  // laisse INTACT même s'il le traverse.
  const _grillePropre = isOmv || !!TENANT_HUB_COLUMNS[tenantSlug];
  const _defaut = HUB_COLUMNS[hubName] || HUB_COLUMNS.commercial;
  const aProfilChantier = Boolean(profilChantier());
  const hubColumns = colonneLibelleDossier(colonnesAvecSite(_colsDeclarees
    || (_grillePropre ? _defaut : colonnesSelonCapacites(_defaut)), aProfilChantier), aProfilChantier);

  // Filtres avancés uniformes — déclarés AVANT loadData (anti-TDZ
  // useCallback dependency order, cf. feedback_react_tdz.md).
  const [filterStatutLocal, setFilterStatutLocal] = useState("all");
  const [filterVisiteurLocal, setFilterVisiteurLocal] = useState("all");
  const [showArchivedLocal, setShowArchivedLocal] = useState(false);
  // PN-8 : cliquer la pastille « Mis de côté » charge ET montre ces dossiers (avant, le filtre ne montrait rien tant que « Inclure archivés » était décoché).
  const [voirMisDeCote, setVoirMisDeCote] = useState(false);
  const inclureArchives = showArchivedLocal || voirMisDeCote;
  // PN-10 : pastille active (suivie pour l'état vide) et clé de remontage de la grille (« Réinitialiser les filtres » vide aussi la recherche et la pastille de OHGrid).
  const [statActif, setStatActif] = useState(null);
  const [grilleKey, setGrilleKey] = useState(0);
  // CC-30 — extension filtres dynamiques (2026-05-07).
  const [filterInstallateurLocal, setFilterInstallateurLocal] = useState("all");
  const [filterMandataireLocal, setFilterMandataireLocal] = useState("all");
  const [filterDeptLocal, setFilterDeptLocal] = useState("all");
  // OMV — filtre Ville dérivé du dataset (entreprise.ville).
  const [filterVilleLocal, setFilterVilleLocal] = useState("all");
  const [filterContratSigneLocal, setFilterContratSigneLocal] = useState("all");  // #557 tri-état
  const [filterVisitePayeeLocal, setFilterVisitePayeeLocal] = useState("all");    // #557 tri-état
  // CC-30 complément (2026-05-07) — 4 filtres bool supplémentaires
  const [filterFacturePayeeLocal, setFilterFacturePayeeLocal] = useState("all");  // #557 tri-état
  const [filterMandatSigneLocal, setFilterMandatSigneLocal] = useState("all");    // #557 tri-état
  const [filterDevisInstLocal, setFilterDevisInstLocal] = useState(false);
  const [filterPhotosLocal, setFilterPhotosLocal] = useState(false);

  // Modal bulk "Appliquer produit AMO" — Hub Finance uniquement (JJ 2026-05-19)
  const [amoModal, setAmoModal] = useState({ open: false, ids: [] });

  // ─── Chargement (useCallback AVANT useEffect — anti-TDZ) ───
  // showArchivedLocal contrôle le query param backend `include_archived`
  // (cf. CC-05 phase b — filtre archive backend rendu conditionnel).
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (presetEffectif) params.append("filter_preset", presetEffectif);
      if (etatsInitiaux) params.append("step", etatsInitiaux);   // T-181 — états d'une tuile du tableau de bord (liste séparée par des virgules)
      params.append("limit", "500");
      if (inclureArchives) params.append("include_archived", "true");
      const url = `${API}/api/metier/hub-grids/grid/${hubName}?${params}`;
      const res = await fetch(url, { headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) {
        const json = await res.json();
        // ID stable pour OHGrid + affichage source_apport dans cellule installateur
        const enriched = (json.dossiers || []).map(d => ({
          ...d,
          id: d.dossier_id || d.référence,
          // Si source_apport=direct → afficher "Apport direct" en lieu de l'installateur null
          installateur_nom: d.source_apport === "direct"
            ? "Apport direct"
            : (d.installateur_nom || ""),
        }));
        // Alerte dev si le dataset est tronqué (tenant > 500 dossiers)
        if (json.total && enriched.length < json.total) {
          console.warn(
            `[HubGrid/${hubName}] Dataset tronqué: ${enriched.length}/${json.total} dossiers chargés. ` +
            `Une pagination serveur sera nécessaire au-delà de 500.`
          );
        }
        setData({ ...json, dossiers: enriched });
      } else toast.error("Erreur chargement grille");
    } catch { toast.error("Erreur réseau"); }
    finally { setLoading(false); }
  }, [hubName, presetEffectif, inclureArchives, etatsInitiaux]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Actions unitaires ───
  const handleRowArchive = useCallback(async (row, action) => {
    const endpoint = action === "archive"
      ? `${API}/api/metier/dossiers/${row.dossier_id}/archiver`
      : `${API}/api/metier/dossiers/${row.dossier_id}/desarchiver`;
    try {
      const res = await fetch(endpoint, { method: "POST", headers: getAuthHeaders(), credentials: "include" });
      if (res.ok) {
        toast.success(action === "archive" ? "Dossier mis de côté" : "Dossier remis en place");
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
  }, [loadData]);

  // ─── Export CSV ───
  const exportCSV = useCallback((rows) => {
    if (!rows || !rows.length) return toast.error("Aucun dossier à exporter");
    const headers = hubColumns.map(c => c.label);
    const csvRows = rows.map(row =>
      hubColumns.map(col => {
        const val = row[col.key];
        if (col.check) return val ? "Oui" : "Non";
        if (col.money) return val ? Number(val).toLocaleString("fr-FR") : "";
        if (col.pct) return `${val || 0}%`;
        if (col.badge) return row.statut_libelle || stepLabels[val] || val || "";
        return val ?? "";
      }).map(v => `"${String(v).replace(/"/g, '""')}"`).join(";")
    );
    const bom = "\uFEFF";
    const csv = bom + [headers.join(";"), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${hubName}_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} dossier(s) exporté(s)`);
  }, [hubColumns, stepLabels, hubName]);

  // ─── Config OHGrid ───
  // Stats = états du tenant (`etats`), cliquables
  const stats = useMemo(() => {
    const total = data.total || 0;
    const hasSteps = (data.etats || []).length > 0;

    // OMV : KPIs PAR STATUT (dossier) + Total — pour comparer aux KPIs de la grille
    // devis et comprendre les écarts. Comptés sur les dossiers chargés, ordonnés par
    // la liste éditable statuts_omv, puis statuts présents hors liste (prospect…).
    if (isOmv) {
      const counts = {};
      (data.dossiers || []).forEach((d) => { const s = d.statut || "—"; counts[s] = (counts[s] || 0) + 1; });
      const ordered = [];
      (omvStatuts || []).forEach((it) => {
        const lbl = it.label || it.id;
        if (counts[lbl] != null) { ordered.push([lbl, counts[lbl]]); delete counts[lbl]; }
      });
      Object.entries(counts).forEach(([s, c]) => ordered.push([s, c]));
      return [
        { key: "all", label: "Total", value: total, color: "blue", filter: null },
        // Archivés : compteur backend (data.archived_count), TOGGLE-INDÉPENDANT (correct
        // même quand "Inclure archivés" est OFF, donc archives non chargées) — comme
        // les prospects. PAS un filtre sur les lignes chargées.
        { key: "archived", label: "Mis de côté", value: data.archived_count ?? 0, color: "amber",
          filter: (row) => row.statut === "archive" || row.statut_global === "archive" },
        // États du tenant renvoyés par le serveur (source unique) ; repli sur le décompte
        // des lignes chargées tant que le tenant n'a aucun état configuré.
        ...(hasSteps
          ? statsDepuisEtats(data.etats)
          : ordered.map(([s, c]) => ({
              key: `omvst_${s}`, label: s, value: c, color: "blue",
              filter: (row) => (row.statut || "—") === s,
            }))),
      ];
    }

    // Décompte ventilé (demande JJ 2026-06-16) : la grille affichait des pastilles d'état
    // (« Créé : 38 ») sans expliquer l'écart avec le total (41) = les dossiers
    // archivés (3), masqués par défaut → confusion. On affiche désormais le trio
    // Total / Actifs / Archivés, cohérent (actifs + archivés = total = badge).
    // Compteurs fournis par le backend (toggle-independent) ; un dossier "archivé"
    // porte statut/statut_global == "archive". Repli si vieux backend sans counts.
    const archivedCount = data.archived_count ?? 0;
    const activeCount = data.active_count ?? Math.max(0, total - archivedCount);
    const isArchivedRow = (r) => r?.statut === "archive" || r?.statut_global === "archive";

    // Pour la vue "Tous les dossiers" : Total / Actifs / Archivés cliquables.
    // Pour les autres hubs, pas de préfixe (le total est implicite, seuls les
    // dossiers du hub sont chargés).
    const prefix = hubName === "all" && hasSteps
      ? [
          { key: "all", label: "Total", value: total, color: "blue", filter: null },
          {
            key: "active",
            label: "Actifs",
            value: activeCount,
            color: "green",
            filter: (row) => !isArchivedRow(row) && row.pipeline_step !== "abandonne",   // PN-10 : « Actifs » exclut aussi les abandonnés
          },
          // Carte "Archivés" TOUJOURS affichée sur la vue dossiers (même à 0) :
          // elle répond à « combien sont masqués ? » et rend le total lisible
          // (actifs + archivés = total). Cliquer filtre sur les archivés (visibles
          // si le toggle "Inclure archivés" est actif — sinon non chargés).
          {
            key: "archived",
            label: "Mis de côté",
            value: archivedCount,
            color: "amber",
            filter: (row) => isArchivedRow(row),
          },
        ]
      : [];

    const stepStats = statsDepuisEtats(data.etats);
    // PN-10 : les dossiers dont l'état n'a pas de phase pour ce tenant (`statuts_sans_phase`) ne tombaient dans aucune pastille : la somme des
    // pastilles était inférieure au total, sans explication. Une pastille « Autres » les porte.
    const sansPhase = data.statuts_sans_phase || {};
    const nAutres = Object.values(sansPhase).reduce((a, n) => a + (Number(n) || 0), 0);
    const autres = nAutres > 0
      ? [{ key: "etat_autres", label: "Autres", value: nAutres, color: "gray", filter: (row) => Object.prototype.hasOwnProperty.call(sansPhase, String(row.statut)) }]
      : [];

    return [...prefix, ...stepStats, ...autres];
  }, [data.statuts_sans_phase, data.etats, data.total, data.active_count, data.archived_count, data.dossiers, stepLabels, hubName, isOmv, omvStatuts]);

  // Colonnes OHGrid dynamiques selon hub
  const columns = useMemo(() => hubColumns.map(col => ({
    key: col.key,
    label: col.label,
    sortable: col.sortable !== false,
    render: (value, row) => {
      if (col.check) return <CheckCell value={value} />;
      if (col.pct) return <PctCell value={value} />;
      if (col.badge) return <BadgeCell value={value} row={row} stepLabels={stepLabels} />;
      if (col.accent) return <AccentCell value={value} />;
      if (col.warning) return <WarningCell value={value} row={row} />;
      if (col.money) return value ? `${Number(value).toLocaleString("fr-FR")} €` : "—";
      if (col.vmc) {
        // type_vmc = multi-choix (tableau) ; rétro-compat valeur mono (str).
        const _vmc = Array.isArray(value) ? value : (value ? [value] : []);
        const _txt = _vmc.map((v) => VMC_LABELS[v] || v).join(" + ") || "—";
        return <span style={{ color: "var(--oh-text)" }}>{_txt}</span>;
      }
      if (col.mono) return <span className="font-mono text-xs">{value ?? "—"}</span>;
      // Auto-détection colonnes date — affiche en JJ/MM/AAAA
      if (col.key.startsWith("date_") || col.key === "created_at" || col.key === "updated_at") {
        return <span style={{ color: col.muted ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{formatDateFR(value)}</span>;
      }
      return <span style={{ color: col.muted ? "var(--oh-text-muted)" : "var(--oh-text)" }}>{value ?? "—"}</span>;
    },
  })), [hubColumns, stepLabels]);

  const rowActions = useMemo(() => [
    { label: "Ouvrir", icon: Eye,
      onClick: (row) => onOpenDossier && onOpenDossier(row.dossier_id, hubName) },
    { label: "Mettre de côté", icon: Archive,
      onClick: (row) => setAMettreDeCote({ ids: [row.dossier_id] }),
      show: (row) => row.pipeline_step !== "archive" && row.statut !== "archive",
      className: "text-[var(--oh-info-text)]" },
    { label: "Remettre en place", icon: ArchiveRestore,
      onClick: (row) => handleRowArchive(row, "unarchive"),
      show: (row) => row.pipeline_step === "archive" || row.statut === "archive",
      className: "text-[var(--oh-accent-text)]" },
  ], [onOpenDossier, handleRowArchive, hubName, setAMettreDeCote]);

  // CC-40 — actions bulk par hub. Le tenantId provient des headers
  // côté request (X-Tenant-ID injecté via getAuthHeaders). En l'absence
  // d'accès direct au tenant ici, on lit localStorage (pattern utilisé
  // ailleurs dans HubGrid).
  const _bulkPatchAndReload = useCallback(async (label, fn, ids) => {
    const tenantId = currentTenantSlug();
    if (!tenantId) { toast.error("Espace non identifié : action impossible"); return; }
    try {
      const n = await fn(ids, tenantId);
      toast.success(`${label} : ${n}/${ids.length} dossier(s) mis à jour`);
      loadData();
    } catch (e) {
      toast.error(`${label} : erreur — ${e.message || e}`);
    }
  }, [loadData]);

  const _bulkArchiveDossiers = useCallback(async (ids, action) => {
    if (!ids.length) return;
    // PN-8 : « mettre de côté » passe par la boîte à raison obligatoire (jamais par un simple confirm).
    if (action === "archive") { setAMettreDeCote({ ids }); return; }
    const verb = action === "unarchive" ? "remettre en place" : "supprimer définitivement";
    if (!window.confirm(`${verb[0].toUpperCase() + verb.slice(1)} ${ids.length} dossier(s) ?${action === "delete" ? " Cette action est irréversible." : ""}`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        const url = action === "delete"
          ? `${API}/api/metier/dossiers/${id}`
          : `${API}/api/metier/dossiers/${id}/desarchiver`;
        const res = await fetch(url, {
          method: action === "delete" ? "DELETE" : "POST",
          headers: getAuthHeaders(), credentials: "include",
        });
        if (res.ok) ok += 1; else fail += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`${ok} dossier(s) ${action === "delete" ? "supprimé(s)" : "remis en place"}${fail ? `, ${fail} échec(s)` : ""}`);
    else toast.error(`${verb} : ${fail} échec(s)`);
    loadData();
  }, [loadData]);

  const _confirmerMiseDeCote = useCallback(async (raison) => {
    const ids = aMettreDeCote?.ids || [];
    setAMettreDeCote(null);
    const { ok, fail } = await mettreDeCote(ids, raison, { api: API, entetes: getAuthHeaders() });
    if (ok) toast.success(`${ok} dossier(s) mis de côté${fail ? `, ${fail} échec(s)` : ""}`);
    else toast.error(`Mise de côté : ${fail} échec(s)`);
    loadData();
  }, [aMettreDeCote, loadData]);

  // JJ 2026-05-19 — bulk création factures Pennylane. Séquentiel pour
  // respecter le rate-limit Pennylane. Confirm avant (création externe).
  const _bulkCreateInvoices = useCallback(async (ids) => {
    if (!ids?.length) return;
    if (!window.confirm(`Créer ${ids.length} facture(s) Pennylane (brouillon) ?`)) return;
    const tenantId = currentTenantSlug();
    if (!tenantId) { toast.error("Espace non identifié : action impossible"); return; }
    const toastId = toast.loading(`Création de ${ids.length} facture(s) Pennylane...`);
    try {
      const { success, failed } = await bulkCreateInvoices(ids, tenantId);
      const msg = `${success}/${ids.length} facture(s) créée(s)${failed.length ? ` · ${failed.length} erreur(s)` : ""}`;
      if (failed.length === 0) toast.success(msg, { id: toastId });
      else if (success === 0) toast.error(msg, { id: toastId });
      else toast.warning(msg, { id: toastId });
      if (failed.length && failed.length <= 5) {
        // Affiche les 5 premières erreurs en détail
        failed.forEach(f => console.error(`[bulk-invoice] ${f.id} : ${f.error}`));
      }
      loadData();
    } catch (e) {
      toast.error(`Erreur batch : ${e.message || e}`, { id: toastId });
    }
  }, [loadData]);

  // OMV — bulk "Créer un devis" (Option 1, JJ 2026-06-08). Crée un devis brouillon
  // par dossier et auto-applique la prime CEE du dossier si présente (→ net 0).
  // La prime/délégataire proviennent des clés cachées émises par hub_grids (gaté omv).
  const _bulkCreateDevis = useCallback(async (ids) => {
    if (!ids?.length) return;
    if (!window.confirm(`Créer ${ids.length} devis OMV (brouillon) ?`)) return;
    const tenantId = currentTenantSlug();
    if (!tenantId) { toast.error("Espace non identifié : action impossible"); return; }
    const byId = Object.fromEntries((data.dossiers || []).map(d => [d.dossier_id || d.id, d]));
    const items = ids.map(id => {
      const d = byId[id] || {};
      return { id, montant_prime_cee: d.montant_prime_cee, delegataire_nom: d.delegataire_nom };
    });
    const toastId = toast.loading(`Création de ${ids.length} devis...`);
    try {
      const { success, failed, primed } = await bulkCreateDevis(items, tenantId);
      const primeMsg = primed ? ` · ${primed} avec prime CEE` : "";
      const msg = `${success}/${ids.length} devis créé(s)${primeMsg}${failed.length ? ` · ${failed.length} erreur(s)` : ""}`;
      if (failed.length === 0) toast.success(msg, { id: toastId });
      else if (success === 0) toast.error(msg, { id: toastId });
      else toast.warning(msg, { id: toastId });
      loadData();
    } catch (e) {
      toast.error(`Erreur batch devis : ${e.message || e}`, { id: toastId });
    }
  }, [loadData, data.dossiers]);

  const pennylaneActif = hasIntegrationPennylane();
  const bulkActions = useMemo(() => {
    const actions = [
    // JJ 2026-05-19 — bulk Hub Finance : "Appliquer produit AMO" + "Facturer
    // la sélection (N)". Catalogue fait foi, pas d'input libre.
    // (Export CSV retiré du bulk : déjà présent en row action)
    ...(hubName === "finance" ? [{
      label: "Appliquer produit AMO", icon: Package,
      onClick: (ids) => setAmoModal({ open: true, ids }),
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]",
    }, ...(pennylaneActif ? [{
      // 🔴 ETI-113-B — « Facturer » = facture Pennylane : SEULEMENT si l'intégration est active.
      label: "Facturer la sélection", icon: Send,
      onClick: (ids) => _bulkCreateInvoices(ids),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]",
    }] : [])] : []),
    // OMV (gating omv_france) — "Créer un devis" : brouillon + auto-prime CEE si présente.
    ...(isOmv ? [{
      label: "Créer un devis", icon: FileText,
      onClick: (ids) => _bulkCreateDevis(ids),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]",
    }] : []),
    // CC-40 — actions bulk AMO (Visite/Facture payée, Contrat AMO, Mandat) : concepts
    // TEF/AMO, masqués pour OMV (gating). TEF/climat inchangés.
    ...(isOmv ? [] : [
    { label: "Cocher Visite payée", icon: CheckCircle2, champ: "visite_payee",
      onClick: (ids) => _bulkPatchAndReload(
        "Visite payée", (i, t) => bulkSetBoolField("visite_payee", true, i, t), ids,
      ),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]" },
    { label: "Cocher Facture payée", icon: CheckCircle2, champ: "facture_payee",
      onClick: (ids) => _bulkPatchAndReload(
        "Facture payée", (i, t) => bulkSetFacturePayee(i, t), ids,
      ),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]" },
    { label: "Cocher Contrat AMO", icon: CheckCircle2, champ: "contrat_signe",
      onClick: (ids) => _bulkPatchAndReload(
        "Contrat AMO", (i, t) => bulkSetSigned("contrat_signe", i, t), ids,
      ),
      className: "border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" },
    { label: "Cocher Mandat", icon: CheckCircle2, champ: "mandat_signe",
      onClick: (ids) => _bulkPatchAndReload(
        "Mandat", (i, t) => bulkSetSigned("mandat_signe", i, t), ids,
      ),
      className: "border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]" },
    ]),
    { label: "Mettre de côté", icon: Archive,
      onClick: (ids) => _bulkArchiveDossiers(ids, "archive"),
      className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]" },
    { label: "Remettre en place", icon: ArchiveRestore,
      onClick: (ids) => _bulkArchiveDossiers(ids, "unarchive"),
      className: "border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)]" },
    // PN-8 : la suppression DÉFINITIVE n'est plus un geste de tous les jours : super admin seulement.
    ...(estSuperAdmin ? [{ label: "Supprimer définitivement", icon: Trash2,
      onClick: (ids) => _bulkArchiveDossiers(ids, "delete"),
      className: "border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]" }] : []),
    ];
    // 🔑 Même périmètre que les colonnes (N°728-7) : un tenant à grille propre garde
    // son jeu actuel ; les autres ne reçoivent que les actions de leurs capacités.
    return _grillePropre ? actions : actionsMasseSelonCapacites(actions);
  }, [hubName, isOmv, _grillePropre, _bulkPatchAndReload, _bulkArchiveDossiers, _bulkCreateInvoices, _bulkCreateDevis, pennylaneActif, estSuperAdmin]);

  const headerActions = useMemo(() => [
    // Refonte P1/HubGrid : bouton "Ajouter un dossier" (manquait pour TEF ET OMV).
    // Navigation via l'event écouté par OmniHub (pas de prop → 0 edit OmniHub.jsx,
    // fichier en périmètre OMV). Visible sur les grilles de dossiers (commercial/all).
    ...(["commercial", "all"].includes(hubName) ? [{
      label: "Ajouter un dossier", icon: Plus, primary: true,
      onClick: () => window.dispatchEvent(new CustomEvent("omnihub:navigate",
        { detail: { hub: "commercial", item: "nouveau-dossier" } })),
      className: "border-[var(--oh-success-border)] text-[var(--oh-success-text)] hover:bg-[var(--oh-success-bg)]",
    }] : []),
    { label: "Export", icon: Download, onClick: () => exportCSV(data.dossiers),
      className: "border-[var(--oh-border)]" },
  ], [hubName, data.dossiers, exportCSV]);

  // (state declared above, before loadData, anti-TDZ)
  const visiteurOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.visiteur_amo) set.add(d.visiteur_amo); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);

  // CC-30 — options dérivées du dataset chargé (filtres dynamiques).
  const installateurOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.installateur_nom) set.add(d.installateur_nom); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);

  // Mandataires lus depuis metier_config_listes.mandataires_tef (refactor
  // 2026-05-10 : plus de Set dérivé des dossiers, l'admin gère la liste
  // dans Gouvernance > Listes).
  const mandataireOptions = useMemo(() => {
    return (configMandataires || [])
      .map(m => ({ value: m.id, label: m.label || m.id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [configMandataires]);

  const deptOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => {
      // ville_cp typique "75001 Paris" ou "Paris (75)" ; code_postal direct.
      const cp = String(d.code_postal || (d.ville_cp || "").match(/\b(\d{5})\b/)?.[1] || "").trim();
      if (cp.length >= 2) set.add(cp.slice(0, 2));
    });
    return Array.from(set).sort().map(v => ({ value: v, label: `Dépt ${v}` }));
  }, [data.dossiers]);

  // ─── Options OMV (gating omv_france) ───
  const omvStatutOptions = useMemo(() =>
    (omvStatuts || []).map(s => ({ value: s.id ?? s.label, label: s.label ?? s.id })),
    [omvStatuts]);
  const omvVilleOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => { if (d.ville) set.add(d.ville); });
    return Array.from(set).sort().map(v => ({ value: v, label: v }));
  }, [data.dossiers]);
  const omvDeptOptions = useMemo(() => {
    const set = new Set();
    (data.dossiers || []).forEach(d => {
      const cp = String(d.cp || "").trim();
      if (cp.length >= 2) set.add(cp.slice(0, 2));
    });
    return Array.from(set).sort().map(v => ({ value: v, label: `Dépt ${v}` }));
  }, [data.dossiers]);

  // Filtres OMV : Statut (liste éditable) + Ville + Département (CP), + archivés.
  // TEF/climat conservent le set complet ci-dessous (inchangé).
  const omvAdvancedFilters = useMemo(() => [
    { key: "filterStatutLocal", label: "Statut", type: "select",
      defaultValue: filterStatutLocal, options: omvStatutOptions },
    { key: "filterVilleLocal", label: "Ville", type: "select",
      defaultValue: filterVilleLocal, options: omvVilleOptions },
    { key: "filterDeptLocal", label: "Département (CP)", type: "select",
      defaultValue: filterDeptLocal, options: omvDeptOptions },
    { key: "showArchivedLocal", label: "Inclure archivés", type: "checkbox",
      defaultValue: showArchivedLocal },
  ], [filterStatutLocal, omvStatutOptions, filterVilleLocal, omvVilleOptions,
      filterDeptLocal, omvDeptOptions, showArchivedLocal]);

  const tefAdvancedFilters = useMemo(() => [
    { key: "filterStatutLocal", label: "Statut", type: "select",
      defaultValue: filterStatutLocal,
      options: optionsStatutDepuisEtats(data.etats) || optionsStatutDossier(stepLabels, configStatuts, data.dossiers) },
    { key: "filterVisiteurLocal", label: "Visiteur", type: "select",
      defaultValue: filterVisiteurLocal,
      options: visiteurOptions },
    // CC-30 — 4 selects dynamiques
    { key: "filterInstallateurLocal", label: "Installateur", type: "select",
      defaultValue: filterInstallateurLocal,
      options: installateurOptions },
    { key: "filterMandataireLocal", label: "Mandataire", type: "select",
      defaultValue: filterMandataireLocal,
      options: mandataireOptions },
    { key: "filterDeptLocal", label: "Département", type: "select",
      defaultValue: filterDeptLocal,
      options: deptOptions },
    // 🔑 #557 — TRI-ÉTAT sur les champs qui portent vraiment trois états.
    // Un checkbox ne sait dire que « Oui » : décoché = pas de filtre, jamais
    // « Non ». Or ce qu'on cherche, c'est ce qui MANQUE.
    // ⛔ « Non » et « Non renseigné » restent DISTINCTS : une visite non payée
    // et une visite dont on ignore si elle l'a été n'appellent pas la même
    // action.
    { key: "filterContratSigneLocal", label: "Contrat AMO", type: "tribool",
      defaultValue: filterContratSigneLocal },
    { key: "filterMandatSigneLocal", label: "Mandat", type: "tribool",
      defaultValue: filterMandatSigneLocal },
    { key: "filterVisitePayeeLocal", label: "Visite payée", type: "tribool",
      defaultValue: filterVisitePayeeLocal },
    // Lu sur `facture_payee_etat`, construit par la PROJECTION (#557) :
    // « pas de facture » n'est pas un impayé, et l'écran ne pouvait pas
    // reconstituer la distinction que `bool(…)` avait effacée.
    { key: "filterFacturePayeeLocal", label: "Facture payée", type: "tribool",
      defaultValue: filterFacturePayeeLocal },
    // ⚠️ CES DEUX-LÀ RESTENT DES CHECKBOX, ET C'EST DÉLIBÉRÉ. « Y a-t-il un
    // devis installateur ? », « y a-t-il des photos ? » sont des questions de
    // PRÉSENCE : il n'existe pas d'état « non renseigné ». Leur donner un
    // tri-état afficherait un choix qui ne rendrait jamais rien — un filtre qui
    // ment est pire qu'un filtre incomplet.
    { key: "filterDevisInstLocal", label: "Devis Inst.", type: "checkbox",
      defaultValue: filterDevisInstLocal },
    { key: "filterPhotosLocal", label: "Photos", type: "checkbox",
      defaultValue: filterPhotosLocal },
    { key: "showArchivedLocal", label: "Inclure archivés", type: "checkbox",
      defaultValue: showArchivedLocal },
  ], [
    filterStatutLocal, filterVisiteurLocal, visiteurOptions, showArchivedLocal,
    stepLabels, configStatuts, data.etats, data.dossiers, filterInstallateurLocal, installateurOptions,
    filterMandataireLocal, mandataireOptions,
    filterDeptLocal, deptOptions, filterContratSigneLocal,
    filterVisitePayeeLocal, filterFacturePayeeLocal, filterMandatSigneLocal,
    filterDevisInstLocal, filterPhotosLocal,
  ]);

  // Gating omv_france : jeu de filtres réduit OMV, sinon set TEF/climat complet.
  // 🔴 N°756-2 — et la retombée par défaut suit les CAPACITÉS, exactement comme
  // les colonnes (même portée : ⛔ pas touché quand le tenant a son propre jeu).
  // 🔴 P-MARIO-19 — la retombée d'un tenant SANS le jeu AMO (Mario) est aussi purgée
  // de ses filtres VIDES ; le tenant AMO (tef) garde son jeu déclaré, à l'identique.
  const _retombee = _grillePropre ? null : filtresSelonCapacites(tefAdvancedFilters);
  const advancedFilters = isOmv
    ? omvAdvancedFilters
    : (_grillePropre ? tefAdvancedFilters
                     : (hasDossierAmo() ? _retombee : sansFiltreVide(_retombee)));

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "filterStatutLocal") setFilterStatutLocal(value);
    if (key === "filterVisiteurLocal") setFilterVisiteurLocal(value);
    if (key === "showArchivedLocal") setShowArchivedLocal(value);
    if (key === "filterInstallateurLocal") setFilterInstallateurLocal(value);
    if (key === "filterMandataireLocal") setFilterMandataireLocal(value);
    if (key === "filterDeptLocal") setFilterDeptLocal(value);
    if (key === "filterVilleLocal") setFilterVilleLocal(value);
    if (key === "filterContratSigneLocal") setFilterContratSigneLocal(value);
    if (key === "filterVisitePayeeLocal") setFilterVisitePayeeLocal(value);
    if (key === "filterFacturePayeeLocal") setFilterFacturePayeeLocal(value);
    if (key === "filterMandatSigneLocal") setFilterMandatSigneLocal(value);
    if (key === "filterDevisInstLocal") setFilterDevisInstLocal(value);
    if (key === "filterPhotosLocal") setFilterPhotosLocal(value);
  }, []);

  // 🔴 P1 — « Site / Adresse » : UN appel (route sites existante) par client DISTINCT de la page, mis en cache — ⛔ pas de N+1 par ligne. Profil chantier seulement.
  const [sitesParId, setSitesParId] = useState({});
  const clientsDejaLus = useRef(new Set());
  useEffect(() => {
    if (!aProfilChantier) return undefined;
    const aLire = [...new Set((data.dossiers || []).filter((d) => d.site_id && d.client_id).map((d) => String(d.client_id)))]
      .filter((c) => !clientsDejaLus.current.has(c));
    if (!aLire.length) return undefined;
    aLire.forEach((c) => clientsDejaLus.current.add(c));
    let vivant = true;
    aLire.forEach((c) => {
      fetch(`${API || ""}/api/metier/clients/${encodeURIComponent(c)}/sites`, { headers: getAuthHeaders(), credentials: "include" })
        .then((r) => (r.ok ? r.json() : { sites: [] }))
        .then((j) => { if (vivant) setSitesParId((m) => ({ ...m, ...Object.fromEntries((j.sites || []).map((s) => [s.id, s])) })); })
        .catch(() => { clientsDejaLus.current.delete(c); });
    });
    return () => { vivant = false; };
  }, [data.dossiers, aProfilChantier]);

  const filteredDossiers = useMemo(() => {
    return (data.dossiers || []).filter(d => {
      if (!inclureArchives && (d.statut === "archive" || d.statut_global === "archive")) return false;
      if (filterStatutLocal !== "all" && d.pipeline_step !== filterStatutLocal && d.statut !== filterStatutLocal) return false;
      if (filterVisiteurLocal !== "all" && d.visiteur_amo !== filterVisiteurLocal) return false;
      // CC-30 — 4 filtres dynamiques + 2 booléens
      if (filterInstallateurLocal !== "all" && d.installateur_nom !== filterInstallateurLocal) return false;
      if (filterMandataireLocal !== "all" && d.mandataire !== filterMandataireLocal) return false;
      // OMV — filtre Ville (entreprise.ville).
      if (filterVilleLocal !== "all" && d.ville !== filterVilleLocal) return false;
      if (filterDeptLocal !== "all") {
        // OMV : CP plat (d.cp) ; TEF : code_postal / ville_cp.
        const cp = String(d.cp || d.code_postal || (d.ville_cp || "").match(/\b(\d{5})\b/)?.[1] || "");
        if (cp.slice(0, 2) !== filterDeptLocal) return false;
      }
      // 🔑 #557 — TRI-ÉTAT. ⛔ Plus de `if (filtre && !d.champ)` : `!valeur` est
      // vrai pour `false`, pour `null` ET pour `undefined` — c'est la classe
      // falsy, et elle rendait « non » et « non renseigné » indiscernables.
      if (!matchTriEtat(filterContratSigneLocal, d.contrat_signe)) return false;
      if (!matchTriEtat(filterMandatSigneLocal, d.mandat_signe)) return false;
      if (!matchTriEtat(filterVisitePayeeLocal, d.visite_payee)) return false;
      // Lu sur l'état construit par la projection, pas sur le booléen aplati.
      if (!matchEtatFacture(filterFacturePayeeLocal, d)) return false;
      // Champs de PRÉSENCE — 2 états assumés (cf. déclaration des filtres).
      if (filterDevisInstLocal && !d.has_devis_installateur) return false;
      if (filterPhotosLocal && !d.has_photos) return false;
      return true;
    });
  }, [
    data.dossiers, inclureArchives, filterStatutLocal, filterVisiteurLocal,
    filterInstallateurLocal, filterMandataireLocal, filterVilleLocal,
    filterDeptLocal, filterContratSigneLocal, filterVisitePayeeLocal,
    filterFacturePayeeLocal, filterMandatSigneLocal,
    filterDevisInstLocal, filterPhotosLocal,
  ]);

  // Lignes de la grille : + `site_libelle` (profil chantier) — mémoïsé pour ne pas changer d'identité à chaque rendu.
  const lignesGrille = useMemo(
    () => (aProfilChantier ? filteredDossiers.map((d) => ({ ...d, site_libelle: libelleSiteDuDossier(d, sitesParId), libelle_dossier: libelleDuDossier(d) })) : filteredDossiers),
    [aProfilChantier, filteredDossiers, sitesParId]);

  // PN-10 : un filtre est « actif » dès qu'un réglage s'écarte de son défaut, y compris une pastille cliquée ou « mis de côté » affiché.
  const filtresActifs = [filterStatutLocal, filterVisiteurLocal, filterInstallateurLocal, filterMandataireLocal, filterDeptLocal, filterVilleLocal,
    filterContratSigneLocal, filterVisitePayeeLocal, filterFacturePayeeLocal, filterMandatSigneLocal].some((v) => v !== "all")
    || filterDevisInstLocal || filterPhotosLocal || showArchivedLocal || !!statActif;
  const reinitialiserFiltres = useCallback(() => {
    setFilterStatutLocal("all"); setFilterVisiteurLocal("all"); setFilterInstallateurLocal("all"); setFilterMandataireLocal("all");
    setFilterDeptLocal("all"); setFilterVilleLocal("all"); setFilterContratSigneLocal("all"); setFilterVisitePayeeLocal("all");
    setFilterFacturePayeeLocal("all"); setFilterMandatSigneLocal("all"); setFilterDevisInstLocal(false); setFilterPhotosLocal(false);
    setShowArchivedLocal(false); setVoirMisDeCote(false); setStatActif(null);
    setGrilleKey((k) => k + 1);
  }, []);

  const searchFilter = useCallback((row, q) => (
    (row.référence || row.reference || "").toLowerCase().includes(q) ||
    (row.client_nom || "").toLowerCase().includes(q) ||
    (row.ville_cp || "").toLowerCase().includes(q) ||
    (row.visiteur_amo || "").toLowerCase().includes(q) ||
    (row.installateur_nom || "").toLowerCase().includes(q) ||
    // OMV : retrouver un dossier par établissement (site travaux) ou par
    // SIRET/SIREN. siret.includes(q) couvre aussi le SIREN (préfixe du SIRET) ;
    // siren explicite pour la saisie 9 ch. Inerte si le row n'a pas ces clés (TEF).
    (row.nom_etablissement || "").toLowerCase().includes(q) ||
    (row.siret || "").includes(q) ||
    (row.siren || "").includes(q)
  ), []);

  return (
    <div className="space-y-4" data-testid={`hub-grid-${hubName}`}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-1 h-6 rounded-full" style={{ background: meta.color }} />
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>{meta.label}</h2>
        <Badge className="text-xs px-2 py-0.5"
          style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}>
          {lignesGrille.length} dossier{lignesGrille.length > 1 ? "s" : ""}
        </Badge>
        {GAMORA_HUB_MAP[hubName] && (
          <GamoraHubBadge hub={GAMORA_HUB_MAP[hubName]} label={meta.label} />
        )}
      </div>

      {/* Sélecteur de presets — fusion des ex-5 menus dossiers en 1 grille.
          Chaque preset = un réglage (filtre statut + colonnes) de CETTE grille.
          La liste presets est gatée en amont (rôle/feature) → un rôle non
          autorisé ne reçoit jamais le preset Finance/Conformité. */}
      {showPresets && (
        <div className="flex items-center gap-2 flex-wrap" role="tablist" aria-label="Vues des dossiers" data-testid="hub-grid-presets">
          {presetsAffiches.map((p) => {
            const active = p.key === hubName;
            return (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`hub-preset-${p.key}`}
                onClick={() => setHubName(p.key)}
                className="text-sm px-3 py-1.5 rounded-lg border transition-colors"
                style={active
                  ? { background: `${meta.color}20`, color: meta.color, borderColor: `${meta.color}55` }
                  : { background: "var(--oh-bg-surface)", color: "var(--oh-text)", borderColor: "var(--oh-border)" }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      )}

      {hubName === "finance" && facturationPennylane && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="hub-finance-facturation">
          <button type="button" aria-pressed={aFacturer} onClick={() => setAFacturer((v) => !v)} data-testid="hub-finance-a-facturer"
            className="text-sm px-3 py-1.5 rounded-lg border"
            style={aFacturer
              ? { background: "var(--oh-accent-bg)", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" }
              : { background: "var(--oh-bg-surface)", color: "var(--oh-text)", borderColor: "var(--oh-border)" }}>
            À facturer
          </button>
          <button type="button" onClick={() => facturationPennylane.ouvrirActions?.()} data-testid="hub-finance-actions-pennylane"
            className="text-sm px-3 py-1.5 rounded-lg border"
            style={{ background: "transparent", color: "var(--oh-accent-text)", borderColor: "var(--oh-accent)" }}>
            Actions de facturation Pennylane
          </button>
        </div>
      )}

      {filtresActifs && (
        <div className="flex items-center gap-3" data-testid="hub-grid-filtres-actifs">
          <span className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            {lignesGrille.length === 0 ? "Aucun dossier avec ces filtres." : "Des filtres sont actifs."}
          </span>
          <Button variant="outline" size="sm" onClick={reinitialiserFiltres} data-testid="hub-grid-reinitialiser-filtres">Réinitialiser les filtres</Button>
        </div>
      )}

      <OHGrid
        key={grilleKey}
        onActiveStatChange={(k) => { setStatActif(k); setVoirMisDeCote(pastilleExigeMisDeCote(k)); }}
        data={lignesGrille}
        columns={columns}
        stats={stats}
        rowActions={rowActions}
        bulkActions={bulkActions}
        headerActions={headerActions}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        selectable={true}
        pagination={true}
        pageSize={25}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => onOpenDossier && onOpenDossier(row.dossier_id, hubName)}
        searchPlaceholder={placeholderRecherche(isOmv)}
        searchFilter={searchFilter}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        emptyMessage={messageVide({ filtresActifs, nbMisDeCote: data.archived_count })}
        testIdPrefix={`hub-${hubName}`}
      />

      {/* Modal bulk "Appliquer produit AMO" — Hub Finance uniquement (JJ 2026-05-19) */}
      <BulkApplyProduitAmoModal
        open={amoModal.open}
        onClose={() => setAmoModal({ open: false, ids: [] })}
        selectedIds={amoModal.ids}
        tenantId={currentTenantSlug()}
        onDone={loadData}
      />

      {/* PN-8 — « Mettre de côté » : la raison est obligatoire, rien n'est effacé */}
      <MettreDeCoteDialog ouvert={!!aMettreDeCote} nombre={aMettreDeCote?.ids?.length || 1}
        onFermer={() => setAMettreDeCote(null)} onConfirm={_confirmerMiseDeCote} />
    </div>
  );
}
